import { EyeStroke, FileMultipleStroke, Pencil1Stroke, Search1Stroke, Trash3Stroke } from '@lineiconshq/free-icons';
import { Lineicons, type LineiconsProps } from '@lineiconshq/react-lineicons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DataTableRenderCellParams, DataTableRowContextMenuConfig, DataTableRowEventParams } from './types';
import { cn } from '@/lib/utils';

const MENU_WIDTH = 210;
const MENU_ITEM_HEIGHT = 30;
const VIEWPORT_GAP = 6;

type PageAction = 'view' | 'edit' | 'delete' | 'copy';

interface RowContextMenuState {
  position: { x: number; y: number };
  cell: DataTableRenderCellParams;
  pageActions: Record<PageAction, boolean>;
}

interface ErpDataTableRowContextMenuProps {
  state: RowContextMenuState | null;
  config?: DataTableRowContextMenuConfig;
  filterLabel: string;
  onClose: () => void;
  onFilter: () => void;
  onPageAction: (action: PageAction, params: DataTableRowEventParams) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: LineiconsProps['icon'];
  onClick: () => void;
}

export function ErpDataTableRowContextMenu({
  state,
  config,
  filterLabel,
  onClose,
  onFilter,
  onPageAction
}: ErpDataTableRowContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<React.CSSProperties>({});

  const rowParams = useMemo<DataTableRowEventParams | null>(
    () => (state ? { id: state.cell.id, row: state.cell.row } : null),
    [state]
  );

  const items = useMemo<MenuItem[]>(() => {
    if (!state || !rowParams) return [];

    const nextItems: MenuItem[] = [];
    const addAction = (
      id: string,
      label: string,
      icon: LineiconsProps['icon'],
      handler: ((params: DataTableRowEventParams) => void | Promise<void>) | undefined,
      pageAction?: PageAction
    ) => {
      if (handler) {
        nextItems.push({ id, label, icon, onClick: () => void handler(rowParams) });
        return;
      }

      if (pageAction && (pageAction === 'view' || state.pageActions[pageAction])) {
        nextItems.push({
          id,
          label,
          icon,
          onClick: () => onPageAction(pageAction, rowParams)
        });
      }
    };

    addAction('open-new-tab', 'Xem trong tab mới', EyeStroke, config?.onOpenInNewTab);
    addAction('view', 'Xem', EyeStroke, config?.onView, 'view');
    addAction('edit', 'Sửa', Pencil1Stroke, config?.onEdit, 'edit');
    addAction('delete', 'Xóa', Trash3Stroke, config?.onDelete, 'delete');
    addAction('copy', 'Sao chép dữ liệu', FileMultipleStroke, config?.onCopy, 'copy');

    if (!config?.hideFilter) {
      nextItems.push({
        id: 'filter',
        label: filterLabel,
        icon: Search1Stroke,
        onClick: onFilter
      });
    }

    return nextItems;
  }, [config, filterLabel, onFilter, onPageAction, rowParams, state]);

  useLayoutEffect(() => {
    if (!state) return;

    const menuHeight = Math.max(items.length * MENU_ITEM_HEIGHT + 6, MENU_ITEM_HEIGHT);
    setPosition({
      left: Math.max(VIEWPORT_GAP, Math.min(state.position.x, window.innerWidth - MENU_WIDTH - VIEWPORT_GAP)),
      top: Math.max(VIEWPORT_GAP, Math.min(state.position.y, window.innerHeight - menuHeight - VIEWPORT_GAP))
    });
  }, [items.length, state]);

  useEffect(() => {
    if (!state) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', onClose);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', onClose);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose, state]);

  if (!state || items.length === 0) return null;

  return (
    <div
      ref={menuRef}
      role='menu'
      aria-label='Thao tác dòng dữ liệu'
      className='fixed z-[1400] w-[210px] overflow-hidden rounded-sm border border-gray-300 bg-white p-[3px] shadow-lg'
      style={position}
    >
      {items.map(item => (
        <button
          key={item.id}
          type='button'
          role='menuitem'
          className={cn(
            'flex h-[30px] w-full cursor-default items-center gap-2 rounded-[2px] border border-transparent px-2 text-left text-xs text-gray-900',
            'hover:border-sky-300 hover:bg-sky-100 hover:text-sky-700 focus:border-sky-300 focus:bg-sky-100 focus:text-sky-700 focus:outline-none'
          )}
          onClick={event => {
            event.stopPropagation();
            item.onClick();
            onClose();
          }}
        >
          <Lineicons aria-hidden='true' icon={item.icon} className='size-[18px] shrink-0 text-main' />
          <span className='min-w-0 flex-1 truncate'>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export type { PageAction, RowContextMenuState };
